
import { useState, useEffect, useMemo } from 'react';
import { getToken } from 'firebase/messaging';
import { AppUser, SpotVisit, CollaborativeMap, AppNotification } from '../types';
import {
  subscribeToAuth,
  subscribeToHiddenSpots,
  subscribeToFavorites,
  subscribeToFavoriteRoutes,
  toggleFavoriteRoute,
  logoutUser,
  messaging,
  updateFcmToken,
  updateUserSettings,
} from '../services/firebaseService';
import { subscribeToVisits, getUserTier, cleanupCorruptedVisits } from '../services/visitService';
import { subscribeToUserCollaborativeMaps } from '../services/collaborativeMapsService';
import { subscribeToNotifications } from '../services/notificationHistoryService';
import { identifyIAP, logoutIAP, getCustomerInfo, hasActiveEntitlement } from '../services/iapService';

export const useFirebaseSync = () => {
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [hiddenSpotIds, setHiddenSpotIds] = useState<string[]>([]);
  const [favoriteSpotIds, setFavoriteSpotIds] = useState<string[]>([]);
  const [favoriteRouteIds, setFavoriteRouteIds] = useState<string[]>([]);
  const [visits, setVisits] = useState<SpotVisit[]>([]);
  const [collaborativeMaps, setCollaborativeMaps] = useState<CollaborativeMap[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);

  useEffect(() => {
    const unsubAuth = subscribeToAuth(u => {
      setCurrentUser(u);
      setAuthReady(true);
    });
    return () => { if (unsubAuth) unsubAuth(); };
  }, []);

  // Initialize RevenueCat IAP on iOS when user logs in, then sync entitlements
  useEffect(() => {
    if (!currentUser?.uid || !currentUser._firestoreReady) return;
    // identifyIAP (not just init): aliases any anonymous RevenueCat id to the
    // Firebase UID so purchases attach to the real account, not a phantom doc.
    identifyIAP(currentUser.uid).then(async () => {
      const info = await getCustomerInfo();
      if (!info) return;
      const entitled = hasActiveEntitlement(info);
      const firestoreSaysApplePaid = currentUser.subscriptionStatus === 'paid'
        && currentUser.subscriptionProvider === 'apple';
      if (entitled && !firestoreSaysApplePaid) {
        updateUserSettings(currentUser.uid, { subscriptionStatus: 'paid', subscriptionProvider: 'apple' });
      } else if (!entitled && firestoreSaysApplePaid) {
        updateUserSettings(currentUser.uid, { subscriptionStatus: 'free', subscriptionProvider: null });
      }
    });
  }, [currentUser?.uid, currentUser?._firestoreReady]);

  useEffect(() => {
    if (currentUser) {
      const unsubHidden = subscribeToHiddenSpots(currentUser.uid, setHiddenSpotIds);
      const unsubFavs = subscribeToFavorites(currentUser.uid, setFavoriteSpotIds);
      const unsubFavRoutes = subscribeToFavoriteRoutes(currentUser.uid, setFavoriteRouteIds);
      const unsubVisits = subscribeToVisits(currentUser.uid, setVisits);
      // One-shot opprydning av besøk med korrupt spotId (akkumulerte
      // wpcard-/visit_-prefiks fra en tidligere kaskade-bug). Kjører idempotent
      // — returnerer null hvis ingenting trenger opprydning.
      cleanupCorruptedVisits(currentUser.uid).catch(err =>
        console.warn('[VISIT-CLEANUP] failed:', err)
      );
      const unsubCollab = subscribeToUserCollaborativeMaps(currentUser.uid, setCollaborativeMaps);
      const unsubNotifs = subscribeToNotifications(currentUser.uid, setNotifications);
      return () => {
        unsubHidden();
        unsubFavs();
        unsubFavRoutes();
        unsubVisits();
        unsubCollab();
        unsubNotifs();
      };
    } else {
      setHiddenSpotIds([]);
      setFavoriteSpotIds([]);
      setFavoriteRouteIds([]);
      setVisits([]);
      setCollaborativeMaps([]);
      setNotifications([]);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.notificationsEnabled || !currentUser.uid || !messaging) return;
    // @ts-ignore
    const vapidKey = import.meta.env?.VITE_VAPID_KEY as string;
    getToken(messaging, { vapidKey })
      .then(token => { if (token) updateFcmToken(currentUser.uid, token); })
      .catch(() => {});
  }, [currentUser?.uid]);

  const handleToggleFavoriteRoute = async (routeId: string) => {
    if (!currentUser) return;
    const isFav = favoriteRouteIds.includes(routeId);
    await toggleFavoriteRoute(routeId, currentUser.uid, isFav);
  };

  // Sync tier from visits to currentUser (backfill for users missing tier in Firestore).
  // KUN oppover: tier kan være hevet av verifiseringer (server setter user.tier via
  // verifiedSpotCount), så vi må aldri overskrive et høyere nivå med en besøk-bare-
  // beregning. Tar med verifiedSpotCount for å matche serverens bidragsscore.
  useEffect(() => {
    if (!currentUser || visits.length === 0) return;
    const computed = getUserTier(visits, currentUser.verifiedSpotCount || 0);
    if (computed.level > (currentUser.tier || 0)) {
      setCurrentUser(prev => prev ? { ...prev, tier: computed.level } : prev);
    }
  }, [visits, currentUser?.uid, currentUser?.verifiedSpotCount]);

  const visitedSpotIds = useMemo(() => visits.map(v => v.spotId), [visits]);
  const unreadNotificationsCount = useMemo(() => notifications.filter(n => !n.isRead).length, [notifications]);

  const handleLogout = async () => {
    await logoutIAP();
    return logoutUser();
  };

  return {
    currentUser,
    setCurrentUser,
    authReady,
    hiddenSpotIds,
    favoriteSpotIds,
    favoriteRouteIds,
    visits,
    visitedSpotIds,
    collaborativeMaps,
    notifications,
    unreadNotificationsCount,
    handleToggleFavoriteRoute,
    logoutUser: handleLogout
  };
};
